import {
  TextField as MuiTextField,
  BaseTextFieldProps,
  InputAdornment,
  OutlinedInputProps,
  useMediaQuery,
} from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

export interface TextFieldProps extends BaseTextFieldProps {
  className?: string;
  /**
   * If true, the input will be of type 'search'
   */
  search?: boolean;
  /**
   * Callback fired when the value is changed.
   *
   * @param event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: OutlinedInputProps['onChange'];
  /**
   * element to be placed at the start of the input
   */
  startAdornment?: React.ReactNode;
  /**
   * element to be placed at the end of the input
   */
  endAdornment?: React.ReactNode;
  /**
   * Props applied to the Input element.
   */
  InputProps?: Partial<OutlinedInputProps>;
}

const TextFieldImpl = React.forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      className,
      variant = 'outlined',
      multiline,
      rows,
      startAdornment,
      endAdornment,
      search,
      InputProps,
      minRows,
      ...props
    },
    ref
  ) => {
    return (
      <MuiTextField
        className={className}
        ref={ref}
        multiline={multiline}
        minRows={multiline ? (minRows ?? 3) : minRows}
        rows={rows}
        inputProps={{ style: { resize: 'vertical' } }}
        {...props}
        InputProps={{
          ...InputProps,
          type: search ? 'search' : (props?.type ?? InputProps?.type),
          ...(startAdornment && {
            startAdornment: (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
          }),
          ...(endAdornment && {
            endAdornment: (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ),
          }),
        }}
      />
    );
  }
);

/**
 * Customized MUI TextField component.
 *
 * It inherits all properties of a MUI TextField component.
 */
export const TextField = muiStyled(TextFieldImpl)(({
  theme,
  error,
  disabled,
}) => {
  return (
    !error &&
    !disabled &&
    css`
      & .MuiFormHelperText-root {
        color: ${theme.palette.text.secondary};
      }
      & .MuiInputLabel-root {
        color: ${theme.palette.text.secondary};
      }
      & .MuiInputLabel-root.Mui-focused {
        color: ${theme.palette.text.primary};
      }
      & label.Mui-focused {
        color: ${theme.palette.text.primary};
      }
      & .MuiInputBase-input {
        color: ${theme.palette.text.secondary};
      }
      & .Mui-focused .MuiInputBase-input {
        color: ${theme.palette.text.primary};
      }
      & .MuiOutlinedInput-root {
        & fieldset {
          border-color: ${theme.palette.divider};
        }
        &:hover fieldset {
          border-color: ${theme.palette.divider};
        }
        &.Mui-focused fieldset {
          border-color: ${theme.palette.divider};
        }
      }
    `
  );
});

export default TextField;
